import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Trash2, Check, X, Eye, Copy } from "lucide-react";

const STATUS_LABELS = {
  pending: { label: "Beklemede", cls: "bg-white/5 text-[#a0aabf]" },
  approved: { label: "Onaylandı", cls: "bg-[#d4af37]/10 text-[#d4af37]" },
  rejected: { label: "Reddedildi", cls: "bg-red-900/30 text-red-300" },
};

export default function AdminDealers() {
  const [items, setItems] = useState([]);
  const [view, setView] = useState(null);
  const [approving, setApproving] = useState(null);
  const [discount, setDiscount] = useState(30);

  const load = () => api.get("/dealers/applications").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const approve = async () => {
    if (!approving) return;
    try {
      const { data } = await api.post(`/dealers/applications/${approving.id}/approve`, { discount_percent: Number(discount) });
      toast.success(`Onaylandı. Giriş bilgileri: ${data.email} / ${data.temp_password} · İndirim: %${data.discount_percent}`, { duration: 15000 });
      setApproving(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const reject = async (a) => {
    if (!window.confirm("Reddedilsin mi?")) return;
    try {
      await api.post(`/dealers/applications/${a.id}/reject`);
      toast.success("Reddedildi");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (a) => {
    if (!window.confirm("Başvuru silinsin mi?")) return;
    try {
      await api.delete(`/dealers/applications/${a.id}`);
      toast.success("Silindi");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div>
      <div className="wb-overline">Yönetim</div>
      <h1 className="wb-h2 mt-2">Bayi Başvuruları</h1>

      <div className="wb-card mt-8 overflow-x-auto">
        <table className="w-full min-w-[720px] text-sm">
          <thead>
            <tr className="border-b border-white/10 text-left text-[#a0aabf] text-xs uppercase tracking-widest">
              <th className="p-4">Başvuran</th>
              <th className="p-4">Firma</th>
              <th className="p-4">Şehir</th>
              <th className="p-4">Durum</th>
              <th className="p-4">Tarih</th>
              <th className="p-4 text-right">İşlemler</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan="6" className="p-8 text-center text-[#a0aabf]">Henüz başvuru yok.</td></tr>
            ) : items.map((a) => {
              const s = STATUS_LABELS[a.status] || STATUS_LABELS.pending;
              return (
                <tr key={a.id} className="border-b border-white/5" data-testid={`row-dealer-${a.id}`}>
                  <td className="p-4">
                    <div>{a.name}</div>
                    <div className="text-xs text-[#a0aabf]">{a.email} · {a.phone}</div>
                  </td>
                  <td className="p-4">{a.company}</td>
                  <td className="p-4 text-[#a0aabf]">{a.city}</td>
                  <td className="p-4"><span className={`text-xs px-2 py-1 ${s.cls}`}>{s.label}</span></td>
                  <td className="p-4 text-[#a0aabf] text-xs">{new Date(a.created_at).toLocaleDateString("tr-TR")}</td>
                  <td className="p-4 text-right space-x-1">
                    <button onClick={() => setView(a)} className="p-2 border border-white/10 hover:border-[#d4af37] hover:text-[#d4af37]"><Eye size={14} /></button>
                    {a.status === "pending" && (
                      <>
                        <button onClick={() => { setApproving(a); setDiscount(30); }} className="p-2 border border-white/10 hover:border-[#d4af37] hover:text-[#d4af37]" data-testid={`approve-${a.id}`}><Check size={14} /></button>
                        <button onClick={() => reject(a)} className="p-2 border border-white/10 hover:border-red-500 hover:text-red-400"><X size={14} /></button>
                      </>
                    )}
                    <button onClick={() => remove(a)} className="p-2 border border-white/10 hover:border-red-500 hover:text-red-400"><Trash2 size={14} /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {approving && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" onClick={() => setApproving(null)}>
          <div className="bg-[#081b3d] border border-white/10 w-full max-w-md p-8" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-serif text-xl">Bayi Onayı</h2>
            <div className="mt-2 text-sm text-[#a0aabf]">{approving.name} · {approving.company}</div>
            <div className="mt-6">
              <label className="text-xs uppercase tracking-widest text-[#a0aabf]">Bayi İndirim Yüzdesi (%)</label>
              <input
                type="number" min="0" max="90" value={discount}
                onChange={(e) => setDiscount(e.target.value)}
                className="wb-input mt-2"
                data-testid="approve-discount"
                autoFocus
              />
              <div className="text-xs text-[#a0aabf] mt-2">
                Bayi girişi yaptığında tüm ürün fiyatları otomatik bu oranda indirimli görünür.
              </div>
            </div>
            <div className="mt-8 flex justify-end gap-3">
              <button onClick={() => setApproving(null)} className="wb-btn-secondary">İptal</button>
              <button onClick={approve} className="wb-btn-primary" data-testid="approve-confirm">Onayla &amp; Şifre Oluştur</button>
            </div>
          </div>
        </div>
      )}

      {view && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" onClick={() => setView(null)}>
          <div className="bg-[#081b3d] border border-white/10 w-full max-w-xl p-8" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-serif text-xl">{view.name}</h2>
            <div className="mt-2 text-xs text-[#a0aabf]">{view.email} · {view.phone}</div>
            <dl className="mt-6 text-sm grid grid-cols-2 gap-3">
              <div><dt className="text-[#a0aabf] text-xs uppercase tracking-widest">Firma</dt><dd className="mt-1">{view.company}</dd></div>
              <div><dt className="text-[#a0aabf] text-xs uppercase tracking-widest">Şehir</dt><dd className="mt-1">{view.city}</dd></div>
            </dl>
            {view.message && (
              <div className="mt-6">
                <dt className="text-[#a0aabf] text-xs uppercase tracking-widest">Mesaj</dt>
                <dd className="mt-1 text-sm whitespace-pre-line">{view.message}</dd>
              </div>
            )}
            {view.temp_password && (
              <div className="mt-6 wb-glass p-4 border-[#d4af37]/30">
                <div className="text-xs uppercase tracking-widest text-[#d4af37]">Bayi Bilgileri</div>
                {view.discount_percent != null && (
                  <div className="mt-2 text-sm">İndirim Yüzdesi: <span className="text-[#d4af37] font-semibold">%{view.discount_percent}</span></div>
                )}
                <div className="mt-2 flex items-center justify-between gap-3 font-mono text-sm">
                  <span>Geçici Şifre: {view.temp_password}</span>
                  <button onClick={() => { navigator.clipboard.writeText(view.temp_password); toast.success("Kopyalandı"); }} className="text-xs text-[#d4af37]"><Copy size={14} /></button>
                </div>
              </div>
            )}
            <div className="mt-8 flex justify-end">
              <button onClick={() => setView(null)} className="wb-btn-secondary">Kapat</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
